import React, { useEffect, useState } from 'react'
import { GestureResponderEvent, ScrollView } from 'react-native'
import { createEditable } from '../helpers'
import { useDebounce } from '../hooks'
import { SearchGrey } from '../../../app/src/assets/icons'
import { MenuItem, Popover } from '@ui-kitten/components'
import { BtInput } from './BtInput'

type PropTypes = {
  isCustomerModel?: boolean
  style?: any
  value: string
  apiSearchFn: (value: any) => Promise<any>
  displayKey: string
  valueKey: string
  onSelection?: (a) => void
  customSettings?: any
  displayFn?: (a) => ''
  options?: any
  onChange?: (a) => void
  collapsableViewHeading?: React.ReactNode
  autoCompleteType?:
    | 'cc-csc'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-number'
    | 'email'
    | 'name'
    | 'password'
    | 'postal-code'
    | 'street-address'
    | 'tel'
    | 'username'
    | 'off'
    | undefined
  SearchResultItem?: React.FunctionComponent<{
    option: string
    onPress: (a) => void
  }> | null
  isSearchBar?: boolean
  expanded?: boolean
  labelLowercase?: boolean
  label: string
  setExpanded: (a) => void
  onChangeText?: (v) => void
  nativeProps?: any
  extra?: React.ReactNode
  error?: any
  ifNotFound?: (a, b?) => void
}

export const BtAutoCompleteInput = ({
  isCustomerModel = false, // Added this flag to send the customer selected object to customer store
  value,
  nativeProps,
  options = [],
  apiSearchFn,
  displayKey = 'name',
  displayFn,
  valueKey = 'id',
  onChange,
  onSelection,
  extra,
  error,
  expanded,
  setExpanded,
  ifNotFound,
  label,
  isSearchBar = false,
  SearchResultItem = null,
  collapsableViewHeading = null,
  autoCompleteType = 'off',
  onChangeText = (v) => {},
  ...props
}: PropTypes) => {
  const [, setFocused] = useState(false)
  const [inputValue, setInputValue] = useState('')

  const debouncedSearchTerm = useDebounce(inputValue, 500)

  const [autocompleteOptions, setAutocompleteOptions] = useState([])
  const [selectedItem, setSelectedItem] = useState(null)
  const [, setShowResults] = useState(false)

  useEffect(() => {
    if (!inputValue) {
      ifNotFound && ifNotFound(false)
      setShowResults(false)
    } else if (selectedItem && isSearchBar) {
      // even when there is a selected item, show the options if the user searches again
      setShowResults(true)
    }
  }, [inputValue, ifNotFound, isSearchBar, selectedItem])

  useEffect(() => {
    let cancelled = false
    setAutocompleteOptions([])
    if (!value) {
      setSelectedItem(null)
    } else {
      if (options.length > 0) {
        const selectedOption = options.find(
          (option) => option[valueKey] === value,
        )
        setAutocompleteOptions(options)
        setSelectedItem(selectedOption)
      } else {
        apiSearchFn(value).then((res) => {
          if (!cancelled) {
            setAutocompleteOptions(res.items)
            setSelectedItem(res.items[0])
            if (onChange) {
              onChange(res.items?.[0]?.[valueKey])
            }
          }
        })
      }
    }
    return () => {
      cancelled = true
    }
    // with all deps it goes into update loop hence disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  useEffect(() => {
    let v = ''
    if (selectedItem) {
      v = selectedItem?.[displayKey]
    } else {
      if (onSelection) {
        onSelection(null)
      }
    }
    !isSearchBar && setInputValue(v) // if its a search bar then dont show the choosen value in it
  }, [selectedItem, displayKey, isSearchBar, onSelection])

  useEffect(() => {
    setAutocompleteOptions([])
    if (apiSearchFn && debouncedSearchTerm) {
      apiSearchFn(debouncedSearchTerm).then((res) => {
        if (!res.items.length) {
          ifNotFound && ifNotFound(true, debouncedSearchTerm)
        } else {
          ifNotFound && ifNotFound(false, debouncedSearchTerm)
        }
        setAutocompleteOptions(res.items)
      })
    } else if (options.length > 0) {
      setAutocompleteOptions(
        options
          .filter((option) =>
            option[displayKey]
              .toLowerCase()
              .startsWith(debouncedSearchTerm.toLowerCase()),
          )
          .slice(0, 5),
      )
    }
    // with all deps it goes into update loop hence disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, apiSearchFn])

  const onSelect = (option) => {
    setSelectedItem(option)
    if (onChange) {
      // added isCustomerModel condition because of customer screen usage
      if (isCustomerModel) {
        onChange(option)
      } else {
        onChange(option[valueKey])
      }
    }
    if (onSelection) {
      onSelection(option)
    }
  }

  return (
    <Popover
      visible={expanded}
      onBackdropPress={() => setExpanded(false)}
      fullWidth={true}
      anchor={() => (
        <BtInput
          label={label}
          onFocus={() => {
            setFocused(true)
            setExpanded(true)
          }}
          onBlur={() => {
            if (!expanded) {
              setFocused(false)
              setExpanded(false)
            }
          }}
          onClear={() => {
            setInputValue('')
            onChangeText('')
          }}
          onChangeText={(val) => {
            setInputValue(val)
            onChangeText(val)
          }}
          editable={isSearchBar || !selectedItem}
          value={inputValue}
          leftComponent={
            isSearchBar && <SearchGrey style={{ height: 20, width: 20 }} />
          }
          autoComplete={autoCompleteType}
          {...props}
        />
      )}
    >
      <ScrollView style={{ maxHeight: 400, width: '100%' }}>
        {autocompleteOptions.length !== 0 && collapsableViewHeading}
        {autocompleteOptions.map((option) => {
          const onPress = (e?: GestureResponderEvent) => {
            setInputValue('')
            e?.preventDefault()
            e?.stopPropagation()
            onSelect(option)
          }
          return SearchResultItem ? (
            <SearchResultItem option={option} onPress={onPress} />
          ) : (
            <MenuItem
              onPress={() => {
                setInputValue('')
                onSelect(option)
              }}
              key={option[valueKey]}
              title={displayFn ? displayFn(option) : option[displayKey]}
            />
          )
        })}
        {extra}
      </ScrollView>
    </Popover>
  )
}

export default createEditable({ Component: BtAutoCompleteInput })
